const FILL_VALUE = -99999.0;

export function isSeamDebugEnabled() {
  return new URLSearchParams(window.location.search).has('seamDebug');
}

function triangleLonSpan(lons) {
  return Math.max(lons[0], lons[1], lons[2]) - Math.min(lons[0], lons[1], lons[2]);
}

function nearMeridian(lon, eps = 1.0) {
  return Math.abs(Math.abs(lon) - 180) <= eps || Math.abs(lon) <= eps;
}

function srcLabel(src) {
  return typeof src === 'number' ? `v${src}` : `lerp(${typeof src[0] === 'number' ? src[0] : '…'})`;
}

export function analyzeSeamTile(z, x, y, entry, scalar, wetMask) {
  const {positions3, indices, vertexSources, nVertsOriginal, nVerts, nTris} = entry;
  const n = positions3.length / 3;

  let lonMin = Infinity;
  let lonMax = -Infinity;
  let latMin = Infinity;
  let latMax = -Infinity;
  for (let i = 0; i < n; i++) {
    const lon = positions3[i * 3];
    const lat = positions3[i * 3 + 1];
    if (lon < lonMin) lonMin = lon;
    if (lon > lonMax) lonMax = lon;
    if (lat < latMin) latMin = lat;
    if (lat > latMax) latMax = lat;
  }

  let meridianVerts = 0;
  let meridianDry = 0;
  let meridianNaN = 0;
  let meridianFill = 0;
  const meridianSamples = [];

  for (let i = 0; i < n; i++) {
    const lon = positions3[i * 3];
    if (!nearMeridian(lon)) continue;
    meridianVerts++;
    const s = scalar[i];
    const w = wetMask[i];
    if (s === FILL_VALUE) meridianFill++;
    if (Number.isNaN(s)) meridianNaN++;
    if (w < 0.999) meridianDry++;
    if (meridianSamples.length < 8) {
      meridianSamples.push({
        i,
        lon: +lon.toFixed(3),
        lat: +positions3[i * 3 + 1].toFixed(3),
        zeta: Number.isFinite(s) ? +s.toFixed(4) : s,
        wetMask: +w.toFixed(3),
        src: vertexSources ? srcLabel(vertexSources[i]) : 'identity'
      });
    }
  }

  let trisNearMeridian = 0;
  let trisWouldDiscardFill = 0;
  let trisCrossingRaw = 0;
  let trisWideNearMeridian = 0;
  const badTriSamples = [];

  for (let t = 0; t < indices.length / 3; t++) {
    const i0 = indices[t * 3];
    const i1 = indices[t * 3 + 1];
    const i2 = indices[t * 3 + 2];
    const lons = [positions3[i0 * 3], positions3[i1 * 3], positions3[i2 * 3]];
    const lats = [positions3[i0 * 3 + 1], positions3[i1 * 3 + 1], positions3[i2 * 3 + 1]];
    const wets = [wetMask[i0], wetMask[i1], wetMask[i2]];
    const scalars = [scalar[i0], scalar[i1], scalar[i2]];

    const near = lons.some(nearMeridian);
    const span = triangleLonSpan(lons);
    if (span > 180) trisCrossingRaw++;
    if (near && span > 30) trisWideNearMeridian++;

    const minWet = Math.min(wets[0], wets[1], wets[2]);
    const wouldDiscard = minWet < 0.999;
    if (near) trisNearMeridian++;
    if (near && wouldDiscard) trisWouldDiscardFill++;

    if (near && (wouldDiscard || span > 90) && badTriSamples.length < 6) {
      badTriSamples.push({
        tri: t,
        lons: lons.map(v => +v.toFixed(2)),
        lats: lats.map(v => +v.toFixed(2)),
        zeta: scalars.map(v => (v === FILL_VALUE ? 'FILL' : +v.toFixed(3))),
        wetMask: wets.map(v => +v.toFixed(3)),
        span: +span.toFixed(2),
        wouldDiscardFill: wouldDiscard
      });
    }
  }

  const report = {
    tile: `${z}/${x}/${y}`,
    split: Boolean(vertexSources),
    verts: {original: nVertsOriginal, render: nVerts},
    tris: nTris,
    lonRange: [+lonMin.toFixed(3), +lonMax.toFixed(3)],
    latRange: [+latMin.toFixed(3), +latMax.toFixed(3)],
    meridianVerts,
    meridianDry,
    meridianFill,
    meridianNaN,
    meridianSamples,
    trisNearMeridian,
    trisWouldDiscardFill,
    trisCrossingRaw,
    trisWideNearMeridian,
    badTriSamples,
    splitStats: entry.seamSplitStats || null
  };

  console.group(`[seam-debug] tile ${z}/${x}/${y}`);
  console.log(report);
  if (trisWouldDiscardFill > 0) {
    console.warn(`  ${trisWouldDiscardFill} meridian-near tris have wetMask < 1 on a corner → fill shader discards`);
  }
  if (trisCrossingRaw > 0) {
    console.warn(`  ${trisCrossingRaw} output tris still have lon span > 180°`);
  }
  if (meridianFill > 0) {
    console.warn(`  ${meridianFill} meridian verts have FILL_VALUE scalar`);
  }
  if (lonMax > 180 || lonMin < -180) {
    console.warn(`  lon outside [-180,180]: ${lonMin.toFixed(2)} .. ${lonMax.toFixed(2)}`);
  }
  if (lonMin < 0 && lonMax > 0 && lonMax <= 180 && lonMin >= -180) {
    console.info(`  using [-180,0] + [0,180] lon branches (${lonMin.toFixed(1)}..${lonMax.toFixed(1)})`);
  }
  console.groupEnd();
  return report;
}

export function logSeamSplitStats(z, x, y, stats) {
  if (!isSeamDebugEnabled()) return;
  console.log(`[seam-debug] split geometry ${z}/${x}/${y}`, stats);
}
